// @flow

import * as React from 'react'
import styled from 'styled-components'
import { FaMobileScreenButton } from 'react-icons/fa6'

import {
  CheckboxInput,
  ControlLabel,
  ErrorMessage,
  FormGroup,
  FormInput,
  SelectInput,
} from '../../../../../infrastructure/components/Formik'
import { DimensionsInput, SettingsSeparator, SpacingSettings } from '../shared'
import {
  LAYOUT_CUSTOM,
  LAYOUT_NUMBER_OF_COLUMNS,
  LAYOUT_NUMBER_OF_ROWS,
  type LayoutSetting,
  type ColumnWidthSettingItem,
  type RowHeightSettingItem,
} from './shared'
import WebshopGridSettingsLayoutButtons from './WebshopGridSettingsLayoutButtons'

const WebshopGridSettings = ({ values, setFieldValue }) => {
  return (
    <div>
      <FormGroup>
        <ControlLabel required>Grid layout</ControlLabel>
        <WebshopGridSettingsLayoutButtons
          value={values.data.layout}
          onChange={(layout: LayoutSetting) => {
            setFieldValue('data.layout', layout)

            const numberOfColumns =
              layout === LAYOUT_CUSTOM
                ? values.data.custom_number_of_columns
                : LAYOUT_NUMBER_OF_COLUMNS[layout]

            const numberOfRows =
              layout === LAYOUT_CUSTOM
                ? values.data.custom_number_of_rows
                : LAYOUT_NUMBER_OF_ROWS[layout]

            setFieldValue(
              'data.column_widths',
              addOrRemoveListItems(
                values.data.column_widths,
                numberOfColumns,
                newColumnWidthItem
              )
            )
            setFieldValue(
              'data.row_heights',
              addOrRemoveListItems(
                values.data.row_heights,
                numberOfRows,
                newRowHeightItem
              )
            )
          }}
        />
        <ErrorMessage name="data.layout" />
      </FormGroup>

      {values.data.layout !== null && (
        <div>
          {values.data.layout === LAYOUT_CUSTOM && (
            <CustomNumberOfColumnsAndRowsContainer>
              <FormGroup>
                <ControlLabel># of columns</ControlLabel>
                <CustomNumberOfColumnsOrRowsInputContainer>
                  <FormInput
                    name="data.custom_number_of_columns"
                    type="number"
                    min={1}
                    step={1}
                    onChange={e => {
                      const customNumberOfColumns =
                        parseInt(e.target.value) || 0

                      setFieldValue(
                        'data.custom_number_of_columns',
                        customNumberOfColumns
                      )

                      setFieldValue(
                        'data.column_widths',
                        addOrRemoveListItems(
                          values.data.column_widths,
                          customNumberOfColumns,
                          newColumnWidthItem
                        )
                      )
                    }}
                  />
                </CustomNumberOfColumnsOrRowsInputContainer>
              </FormGroup>
              <FormGroup>
                <ControlLabel># of rows</ControlLabel>
                <CustomNumberOfColumnsOrRowsInputContainer>
                  <FormInput
                    name="data.custom_number_of_rows"
                    type="number"
                    min={1}
                    step={1}
                    onChange={e => {
                      const customNumberOfRows = parseInt(e.target.value) || 0

                      setFieldValue(
                        'data.custom_number_of_rows',
                        customNumberOfRows
                      )

                      setFieldValue(
                        'data.row_heights',
                        addOrRemoveListItems(
                          values.data.row_heights,
                          customNumberOfRows,
                          newRowHeightItem
                        )
                      )
                    }}
                  />
                </CustomNumberOfColumnsOrRowsInputContainer>
              </FormGroup>
            </CustomNumberOfColumnsAndRowsContainer>
          )}

          <FormGroup>
            <ControlLabel>Column widths</ControlLabel>
            <ColumnsOrRowsListContainer>
              {values.data.column_widths.map(
                (columnWidth, columnWidthIndex) => (
                  <ColumnOrRowSettingsContainer key={columnWidthIndex}>
                    <div>#{columnWidthIndex + 1}</div>
                    <ColumnWidthModeSelectContainer>
                      <SelectInput
                        name={`data.column_widths.${columnWidthIndex}.mode`}
                        options={columnWidthModeOptions}
                        simpleValue
                        clearable={false}
                      />
                    </ColumnWidthModeSelectContainer>
                    {values.data.column_widths[columnWidthIndex].mode ===
                      'dynamic' && (
                      <>
                        <ColumnOrRowValueInputContainer>
                          <FormInput
                            name={`data.column_widths.${columnWidthIndex}.dynamic_value`}
                            type="number"
                            min={1}
                            step={1}
                          />
                        </ColumnOrRowValueInputContainer>
                        <div>unit(s)</div>
                      </>
                    )}
                    {values.data.column_widths[columnWidthIndex].mode ===
                      'fixed' && (
                      <>
                        <ColumnOrRowValueInputContainer>
                          <FormInput
                            name={`data.column_widths.${columnWidthIndex}.fixed_value`}
                          />
                        </ColumnOrRowValueInputContainer>
                        <div>px</div>
                      </>
                    )}
                  </ColumnOrRowSettingsContainer>
                )
              )}
            </ColumnsOrRowsListContainer>
          </FormGroup>

          <FormGroup>
            <ControlLabel>Row heights</ControlLabel>
            <ColumnsOrRowsListContainer>
              {values.data.row_heights.map((rowHeight, rowHeightIndex) => (
                <ColumnOrRowSettingsContainer key={rowHeightIndex}>
                  <div>#{rowHeightIndex + 1}</div>
                  <RowHeightModeSelectContainer>
                    <SelectInput
                      name={`data.row_heights.${rowHeightIndex}.mode`}
                      options={rowHeightModeOptions}
                      simpleValue
                      clearable={false}
                    />
                  </RowHeightModeSelectContainer>
                  {values.data.row_heights[rowHeightIndex].mode === 'fixed' && (
                    <>
                      <ColumnOrRowValueInputContainer>
                        <FormInput
                          name={`data.row_heights.${rowHeightIndex}.fixed_value`}
                        />
                      </ColumnOrRowValueInputContainer>
                      <div>px</div>
                    </>
                  )}
                </ColumnOrRowSettingsContainer>
              ))}
            </ColumnsOrRowsListContainer>
          </FormGroup>

          <FormGroup>
            <ControlLabel>Max width</ControlLabel>
            <DimensionsInput
              modeName="data.max_width_mode"
              fixedName="data.fixed_max_width"
              parentPercentageName="data.percentage_max_width"
              values={values}
            />
          </FormGroup>

          <GapSettingsContainer>
            <FormGroup>
              <ControlLabel>Columns gap (px)</ControlLabel>
              <FormInput name="data.column_gap" />
            </FormGroup>
            <FormGroup>
              <ControlLabel>Rows gap (px)</ControlLabel>
              <FormInput name="data.row_gap" />
            </FormGroup>
          </GapSettingsContainer>

          <FormGroup>
            <CheckboxInput
              id="data.stack_on_mobile"
              name="data.stack_on_mobile"
              label={
                <>
                  Stack on mobile{' '}
                  <FaMobileScreenButton
                    color="black"
                    size={16}
                    style={{ verticalAlign: 'middle' }}
                  />
                </>
              }
            />
          </FormGroup>

          <SettingsSeparator />

          <SpacingSettings />
        </div>
      )}
    </div>
  )
}

export default WebshopGridSettings

const addOrRemoveListItems = (
  currentList: Array<Object>,
  targetItemsCount: number,
  newItem: Object
) => {
  let newList = currentList

  if (newList.length < targetItemsCount) {
    newList = [
      ...newList,
      ...Array.from({ length: targetItemsCount - newList.length }, () => ({
        ...newItem,
      })),
    ]
  } else if (newList.length > targetItemsCount) {
    newList = newList.slice(0, targetItemsCount)
  }

  return newList
}

const newColumnWidthItem: ColumnWidthSettingItem = {
  mode: 'dynamic',
  dynamic_value: 1,
  fixed_value: 100,
}

const columnWidthModeOptions = [
  { label: 'Dynamic', value: 'dynamic' },
  { label: 'Fixed', value: 'fixed' },
]

const rowHeightModeOptions = [
  { label: 'Auto', value: 'auto' },
  { label: 'Fixed', value: 'fixed' },
]

const newRowHeightItem: RowHeightSettingItem = {
  mode: 'auto',
  fixed_value: 100,
}

const ColumnsOrRowsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ColumnOrRowSettingsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`

const ColumnWidthModeSelectContainer = styled.div`
  flex: 0 0 90px;
`

const RowHeightModeSelectContainer = styled.div`
  flex: 0 0 70px;
`

const ColumnOrRowValueInputContainer = styled.div`
  flex: 0 0 60px;
`

const GapSettingsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 20px;
`

const CustomNumberOfColumnsAndRowsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-content: start;
  gap: 20px;
`

const CustomNumberOfColumnsOrRowsInputContainer = styled.div`
  width: 60px;
`
